"use client";

import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Card,
  TableContainer,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useNavigate } from "react-router-dom";

interface Tone {
  rbtCode: string;
  rbtName: string;
  artistName: string;
  playPath: string;
  expiryDate: string;
}

interface Props {
  myToneList: Tone[];
  successMessage?: string;
  errorMessage?: string;
  onDelete?: (rbtCode: string) => void;
  onSet?: (tone: Tone) => void;
}

const MyTonesLibrary: React.FC<Props> = ({
  myToneList,
  successMessage,
  errorMessage,
  onDelete,
  onSet,
}) => {
  const [currentPlaying, setCurrentPlaying] = useState<number | null>(null);
  const audioRefs = useRef<(HTMLAudioElement | null)[]>([]);
  const navigate = useNavigate();

  const handlePlayPause = (index: number) => {
    const audio = audioRefs.current[index];
    if (!audio) return;

    if (currentPlaying === index) {
      audio.pause();
      setCurrentPlaying(null);
    } else {
      if (currentPlaying !== null && audioRefs.current[currentPlaying]) {
        audioRefs.current[currentPlaying]?.pause();
      }
      audio.play();
      setCurrentPlaying(index);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card
        sx={{
          p: 3,
          boxShadow: 3,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h5"
          align="center"
          sx={{
            color: "#0085ca",
            fontFamily: "sans-serif",
            mb: 2,
            fontWeight: 600,
          }}
        >
          My Tones Library
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#0085ca",
                }}
              >
                <TableCell width="10%"></TableCell>
                <TableCell
                  width="40%"
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  Song # Expiry Date
                </TableCell>
                <TableCell width="30%"></TableCell>
                <TableCell
                  width="10%"
                  sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}
                >
                  Set
                </TableCell>
                <TableCell
                  width="10%"
                  sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}
                >
                  Delete
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {myToneList.map((tone, index) => (
                <TableRow
                  key={tone.rbtCode}
                  hover
                  sx={{
                    "&:hover": {
                      backgroundColor: "#f1f9ff",
                    },
                  }}
                >
                  {/* --- Audio Control --- */}
                  <TableCell>
                    <IconButton
                      onClick={() => handlePlayPause(index)}
                      sx={{ color: "#0087ca57" }}
                    >
                      {currentPlaying === index ? (
                        <PauseIcon />
                      ) : (
                        <PlayArrowIcon />
                      )}
                    </IconButton>
                    <audio
                      ref={(el) => (audioRefs.current[index] = el)}
                      src={tone.playPath}
                      onEnded={() => setCurrentPlaying(null)}
                    />
                  </TableCell>

                  {/* --- Tone Info --- */}
                  <TableCell
                    sx={{
                      color: "#0085ca",
                      fontFamily: "sans-serif",
                      fontSize: 15,
                      // maxWidth: 180,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        overflow: "visible",
                        whiteSpace: "normal",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>
                      {tone.rbtName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tone.artistName}
                    </Typography>
                    <Typography variant="body2" color="error">
                      {tone.expiryDate.slice(0, 10)}
                    </Typography>
                  </TableCell>

                  <TableCell></TableCell>

                  {/* --- Set --- */}
                  <TableCell>
                    <IconButton
               onClick={() => navigate(`/flowtone/setRbt?rbtCode=${tone?.rbtCode}&rbtName=${tone?.rbtName}`)}
                      title="Set RBT"
                      sx={{ color: "#0085ca" }}
                    >
                      <ArrowForwardIosIcon />
                    </IconButton>
                  </TableCell>

                  {/* --- Delete --- */}
                  <TableCell>
                    <IconButton
                      onClick={() => {
                        if (
                          window.confirm(
                            "Are you sure that you want to delete this tone?"
                          )
                        ) {
                          onDelete?.(tone.rbtCode);
                        }
                      }}
                      title="Delete"
                      sx={{ color: "#0085ca" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default MyTonesLibrary;
